import { Play, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import videojs from "video.js";
import "videojs-contrib-ads";
import "videojs-ima";
import ukrainian from "video.js/dist/lang/uk.json";
import "video.js/dist/video-js.css";
import "videojs-contrib-ads/dist/videojs.ads.css";
import "videojs-ima/dist/videojs.ima.css";
import { loadIma } from "./ima";
import type { Video } from "./types";

videojs.addLanguage("uk", ukrainian);

type ContentPlayer = ReturnType<typeof videojs> & {
  ima: {
    (options: Record<string, unknown>): void;
    initializeAdDisplayContainer: () => void;
  };
  ads?: { isInAdMode: () => boolean };
};
type PlayerProps = {
  video: Video | null;
  onClose: () => void;
  onWatched: (id: string, pct: number) => void;
};

export function Player({ video, onClose, onWatched }: PlayerProps) {
  if (!video) return null;
  return <Playback key={video.id} video={video} onClose={onClose} onWatched={onWatched} />;
}

function Playback({ video, onClose, onWatched }: PlayerProps & { video: Video }) {
  const host = useRef<HTMLDivElement>(null);
  const playerRef = useRef<ContentPlayer | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const callbacks = useRef({ onClose, onWatched });
  callbacks.current = { onClose, onWatched };
  const [ready, setReady] = useState(false);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const previousFocus = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    let cancelled = false;
    let player: ContentPlayer | undefined;
    let lastSaved = 0;
    const saveProgress = () => {
      if (!player || player.isDisposed() || player.ads?.isInAdMode?.()) return;
      const duration = player.duration() ?? 0;
      const current = player.currentTime() ?? 0;
      if (duration > 0 && Number.isFinite(duration) && current > 0) {
        callbacks.current.onWatched(video.id, Math.min(100, current / duration * 100));
      }
    };
    const setup = async () => {
      if (!video.playback) return;
      // Blocked ad SDKs must not prevent content playback.
      const imaAvailable = video.adTagUrl ? await loadIma() : false;
      if (cancelled || !host.current) return;
      const element = document.createElement("video-js");
      element.className = "video-js vjs-big-play-centered";
      host.current.appendChild(element);
      player = videojs(element, {
        controls: true, autoplay: false, preload: "metadata", playsinline: true,
        responsive: true, language: "uk", poster: video.thumbnail,
        sources: [video.playback], playbackRates: [0.75, 1, 1.25, 1.5, 2]
      }) as ContentPlayer;
      playerRef.current = player;
      if (imaAvailable && video.adTagUrl) {
        const tag = new URL(video.adTagUrl);
        tag.searchParams.set("correlator", String(Date.now()));
        player.ima({ adTagUrl: tag.toString(), locale: "uk", adLabel: "Реклама", adLabelNofN: "з",
          vastLoadTimeout: 8000, contribAdsSettings: { timeout: 10000, prerollTimeout: 10000 } });
      }
      player.on("error", () => setError("Не вдалося відтворити відео. Спробуйте відкрити його ще раз."));
      player.on("timeupdate", () => {
        if (Date.now() - lastSaved < 5000) return;
        lastSaved = Date.now();
        saveProgress();
      });
      player.on("pause", saveProgress);
      player.on("ended", saveProgress);
      setReady(true);
    };
    void setup().catch(() => !cancelled && setError("Не вдалося завантажити плеєр. Спробуйте ще раз."));
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" || event.key === "Backspace") {
        event.preventDefault();
        callbacks.current.onClose();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      cancelled = true;
      window.removeEventListener("keydown", handleKey);
      saveProgress();
      player?.dispose();
      playerRef.current = null;
      previousFocus?.focus();
    };
  }, [video]);

  const start = () => {
    const player = playerRef.current;
    if (!player) return;
    // IMA initialization must run directly in the user gesture on mobile.
    player.ima?.initializeAdDisplayContainer?.();
    setStarted(true);
    void player.play()?.catch(() => setStarted(false));
    player.el().querySelector<HTMLButtonElement>(".vjs-play-control")?.focus();
  };

  return (
    <div className="playerShade" role="dialog" aria-modal="true" aria-label="Відеоплеєр">
      <section className="playerShell">
        <div className="playerTop">
          <div><p className="microLabel">Клопотенко TV</p><h2>{video.title}</h2></div>
          <button ref={closeRef} className="iconButton closeButton" onClick={onClose} aria-label="Закрити плеєр"><X size={24} /></button>
        </div>
        <div className="playerStage">
          <div ref={host} className="contentPlayer" />
          {!video.playback || error ? (
            <div className="playbackMessage" role="status">{error || "Це відео поки недоступне. Оберіть інший випуск."}</div>
          ) : !started ? (
            <div className="playbackStart"><button className="primaryAction" onClick={start} disabled={!ready}>
              <Play size={24} fill="currentColor" />{ready ? "Відтворити" : "Завантажуємо відео"}
            </button></div>
          ) : null}
        </div>
        <div className="playerMeta"><p>Рецепти та кулінарні історії від Євгена Клопотенка. Смачного перегляду!</p></div>
      </section>
    </div>
  );
}
